import { useTranslations } from '@vocab/react';
import { Badge, Box, Inline, Text, TooltipRenderer } from 'braid-design-system';
import type React from 'react';

import { DraftStatus, JobStatus as Status } from 'src/graphql/__generated__';

import { Skeleton } from '../Skeleton/Skeleton';

import translations from './.vocab';

export interface DraftJobStatusProps {
  isDraft: true;
  loading?: boolean;
  status?: string;
}

export interface PostedJobStatusProps {
  jobId: string;
  isDraft: false;
  daysRemaining?: number;
  status?: Status;
  loading?: boolean;
}

export type Props = DraftJobStatusProps | PostedJobStatusProps;

export const JobStatus: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { t } = useTranslations(translations);

  if (props.loading) {
    return <Skeleton loading numberOfCharacters={8} />;
  }

  if (props.isDraft) {
    const isPendingDraft = props.status === DraftStatus.PENDING;
    return (
      <Badge tone={isPendingDraft ? 'positive' : 'caution'}>
        {isPendingDraft ? t('Pending') : t('Draft')}
      </Badge>
    );
  }

  const { status, daysRemaining, jobId } = props;

  switch (status) {
    case Status.EXPIRED:
      return <Badge tone="neutral">{t('Expired')}</Badge>;
    case Status.BLOCKED:
      return (
        <Inline space="small">
          <TooltipRenderer
            id={`tooltip-${jobId}`}
            tooltip={<Text size="small">{t('Blocked tooltip')}</Text>}
          >
            {({ triggerProps }) => (
              <Box aria-label="Help" {...triggerProps}>
                <Box pointerEvents="none">
                  <Badge tone="critical">{t('Blocked')}</Badge>
                </Box>
              </Box>
            )}
          </TooltipRenderer>
        </Inline>
      );
    case Status.PENDING:
    case Status.PENDING_ACCOUNT_APPROVAL:
    case Status.SUSPENDED:
      return <Badge tone="caution">{t('Pending')}</Badge>;
    case Status.ACTIVE:
      if (!daysRemaining) {
        return <Badge tone="info">{t('Active')}</Badge>;
      }
      return <Text size="small">{t('days left', { daysRemaining })}</Text>;
    default:
      return null;
  }
};
